import { Button, Card, Input, Space, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import { api } from '../../api/client';

const KEYS = {
  passengerShareFriendIos: 'passenger_share_friend_url',
  passengerShareFriendAndroid: 'passenger_share_friend_url_android',
  passengerShareDriver: 'passenger_share_driver_referral_url',
  driverShareFriendIos: 'driver_share_friend_url',
  driverShareFriendAndroid: 'driver_share_friend_url_android',
  driverShareDriver: 'driver_share_driver_referral_url',
} as const;

type FormState = Record<keyof typeof KEYS, string>;

const EMPTY: FormState = {
  passengerShareFriendIos: '',
  passengerShareFriendAndroid: '',
  passengerShareDriver: '',
  driverShareFriendIos: '',
  driverShareFriendAndroid: '',
  driverShareDriver: '',
};

async function loadSetting(key: string): Promise<string> {
  const { data } = await api.get<{ settingValue?: string }>(`/admin/settings/${key}`);
  return data.settingValue?.trim() ?? '';
}

export default function AppShareUrlsPage() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const entries = await Promise.all(
          (Object.entries(KEYS) as [keyof typeof KEYS, string][]).map(async ([field, key]) => {
            const value = await loadSetting(key).catch(() => '');
            return [field, value] as const;
          }),
        );
        setForm({ ...EMPTY, ...Object.fromEntries(entries) });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Card loading={loading}>
      <Typography.Title level={4}>App 分享連結</Typography.Title>
      <Typography.Paragraph type="secondary">
        「向朋友介紹搭的」分 iOS／Android（例如 App Store 與 Google Play 連結）。「司機介紹我用搭的」共用一個
        URL。留空則 App 提示尚未設定；若只填一端，App 會 fallback 用另一端的連結。
      </Typography.Paragraph>

      <Typography.Title level={5}>乘客 App</Typography.Title>
      <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 720 }}>
        <div>
          <Typography.Text>向朋友介紹搭的 — iOS</Typography.Text>
          <Input
            value={form.passengerShareFriendIos}
            onChange={(e) => setField('passengerShareFriendIos', e.target.value)}
            placeholder="https://apps.apple.com/..."
          />
        </div>
        <div>
          <Typography.Text>向朋友介紹搭的 — Android</Typography.Text>
          <Input
            value={form.passengerShareFriendAndroid}
            onChange={(e) => setField('passengerShareFriendAndroid', e.target.value)}
            placeholder="https://play.google.com/..."
          />
        </div>
        <div>
          <Typography.Text>司機介紹我用搭的</Typography.Text>
          <Input
            value={form.passengerShareDriver}
            onChange={(e) => setField('passengerShareDriver', e.target.value)}
            placeholder="https://..."
          />
        </div>
      </Space>

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        司機 App
      </Typography.Title>
      <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 720 }}>
        <div>
          <Typography.Text>向朋友介紹搭的司機版 — iOS</Typography.Text>
          <Input
            value={form.driverShareFriendIos}
            onChange={(e) => setField('driverShareFriendIos', e.target.value)}
            placeholder="https://apps.apple.com/..."
          />
        </div>
        <div>
          <Typography.Text>向朋友介紹搭的司機版 — Android</Typography.Text>
          <Input
            value={form.driverShareFriendAndroid}
            onChange={(e) => setField('driverShareFriendAndroid', e.target.value)}
            placeholder="https://play.google.com/..."
          />
        </div>
        <div>
          <Typography.Text>司機介紹我用搭的司機版</Typography.Text>
          <Input
            value={form.driverShareDriver}
            onChange={(e) => setField('driverShareDriver', e.target.value)}
            placeholder="https://..."
          />
        </div>
      </Space>

      <Button
        type="primary"
        style={{ marginTop: 24 }}
        loading={saving}
        onClick={async () => {
          setSaving(true);
          try {
            await Promise.all(
              (Object.entries(KEYS) as [keyof typeof KEYS, string][]).map(([field, key]) =>
                api.put(`/admin/settings/${key}`, { value: form[field].trim() }),
              ),
            );
            message.success('已更新');
          } finally {
            setSaving(false);
          }
        }}
      >
        儲存
      </Button>
    </Card>
  );
}
